/**
 * The shared screen: a television, or a laptop in the middle of the table.
 *
 * All this adds around `TableView` is the chrome a room needs when nobody is
 * holding it — the code large enough to read from across the room, a QR to
 * scan instead of typing, and a plain statement of what the room is waiting
 * for. The table itself is the same component a player sees behind their hand.
 *
 * It is a spectator, so it is drawn unrotated: seat 0 at the bottom. Rotating
 * a shared screen to somebody's chair would be rotating it away from everybody
 * else's.
 */

import type { MatchView } from '../net/match.js';
import type { GameLayoutWire, ZoneSpecWire } from '../net/session.js';
import { QrCode } from '../render/QrCode.js';
import { TableView } from './TableView.js';
import { Observers } from './Observers.js';

export function TableScreen({
  view,
  layout,
  zones,
  roomCode,
  gameName,
}: {
  view: MatchView;
  layout: GameLayoutWire;
  zones: ZoneSpecWire[];
  roomCode: string;
  gameName: string;
}) {
  /*
   * Where a player should point their phone.
   *
   * A real route, not a query parameter: the `?room=` form is gone and there is
   * deliberately no redirect for it, because nothing outside this machine has
   * ever seen one.
   */
  const joinUrl = `${window.location.origin}/join/${roomCode}`;
  const plain = joinUrl.replace(/^https?:\/\//, '');

  return (
    <div className="table-screen" data-game={layout.gameId}>
      <header className="table-header">
        <div className="room">
          <span className="room-label">Room code</span>
          <span className="room-code">{roomCode}</span>
          <span className="room-hint">{plain}</span>
        </div>
        <div className="table-title">
          <h1>{gameName}</h1>
          {layout.objective ? <p className="objective">{layout.objective}</p> : null}
        </div>
        <div className="table-status">
          {view.round > 0 ? <span className="pill">Round {view.round}</span> : null}
          {view.paused ? <span className="pill warn">Paused</span> : null}
          {!view.connected ? <span className="pill warn">Reconnecting…</span> : null}
          {/* Scanning it skips typing the code, which is the part people get
              wrong when reading letters off a television. */}
          <QrCode text={joinUrl} />
        </div>
      </header>

      <div className="table-body">
        {view.lifecycle === 'lobby' ? (
          <div className="table-waiting">
            <p className="table-waiting-title">
              {view.seats.length >= view.seatCount
                ? 'Waiting for the host to deal'
                : `Waiting for ${view.seatCount - view.seats.length} more`}
            </p>
            <p className="table-waiting-sub">
              Go to <strong>{plain}</strong> and enter <strong>{roomCode}</strong>
            </p>
          </div>
        ) : (
          <TableView view={view} layout={layout} zones={zones} variant="full" />
        )}
      </div>

      {/* A shared screen holds no authority, so it lists watchers but offers no
          way to remove one. That control belongs on the host device. */}
      <Observers view={view} />
    </div>
  );
}
